/**
 * An address belonging to a module this agency does not hold.
 *
 * **The address is not wrong; the module is absent.** Those are two different things and they need
 * two different screens. Sending somebody who typed `/app/development` to the letting dashboard
 * says the address was a mistake, which is the one explanation that is not true — and it is the
 * explanation they will act on, by typing it again.
 *
 * So the screen stays where they are, keeps the rail they do have, and says plainly which module
 * is missing and how to get it. That is the same treatment as everything else shut in the product:
 * *show the locked door, do not hide it* — the advert at the foot of the rail (ADR-0114) and this
 * screen are the two halves of one idea, and pressing either opens the same pricing modal.
 *
 * **It reads which module owns the address, rather than being told.** `moduleForPath()` already
 * decides that for the rail, from the address alone, and a second list here would be a second
 * answer to disagree with the first the day somebody adds a prefix. One guard on the layout route
 * therefore covers **both directions** — a letting-only agency typing a development address and a
 * development-only agency typing a letting one — which is what "neither module is privileged"
 * means when it is enforced rather than asserted.
 *
 * **It decides from the session and speaks with the server's words.** Which modules the agency
 * holds is on the session already, so there is no moment where a development screen is drawn and
 * then snatched away. The `Gate` behind the padlock comes from `GET /settings` like every other
 * gate in the product, and arrives a beat later; the page is complete without it and gains the
 * modal when it lands.
 */

import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { MODULE_INFO, homeFor, moduleForPath, type ModuleCode } from "@rentorng/core";
import { useSession } from "@/lib/session";
import { useModuleGate } from "@/lib/gating";
import { PricingModal } from "@/components/padlock";
import { IconLock } from "@/components/icons";
import { usePageTitle } from "@/lib/title";

/**
 * The screen itself.
 *
 * The wording is the product's own and is deliberately not written twice. "**X** is not part of
 * this account" is the sentence the API answers with — `refuse()` in `apps/api/src/lib/modules.ts`
 * — and the description under it is `MODULE_INFO[code].blurb`, which is the same source that
 * message is assembled from. Nothing here invents a way of saying it, so the refusal a request
 * gets and the screen an address gets cannot drift into two accounts of one fact.
 */
export function ModuleLockedPage({ code }: { code: ModuleCode }) {
  const info = MODULE_INFO[code];
  const gate = useModuleGate(code);
  const { modules } = useSession();
  const [open, setOpen] = useState(false);

  usePageTitle(`${info.label} is not part of this account`);

  return (
    <div className="mx-auto max-w-xl py-10 text-center" data-module-locked={code}>
      <span className="mx-auto grid size-14 place-items-center rounded-full bg-brand-50 text-brand-700">
        <IconLock size={26} />
      </span>

      <h1 className="mt-4 text-xl font-semibold text-ink-900">{info.label} is not part of this account.</h1>
      <p className="mt-2 text-sm text-ink-600">{info.blurb}</p>

      {/*
        Not an error, and drawn so it does not read as one.

        Nothing has failed and nothing has been taken away: this agency bought one half of the
        product and is standing at the door of the other. The brand disc rather than the danger
        red, and a way in rather than a way back, is the whole difference between an advert and a
        rejection.
      */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {gate ? (
          <>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setOpen(true)}
              data-padlock={gate.reason}
              data-padlock-subject={gate.subject}
            >
              <IconLock size={16} />
              What {info.label.toLowerCase()} does
            </button>
            <PricingModal gate={gate} open={open} onClose={() => setOpen(false)} />
          </>
        ) : null}

        {/* Where they can actually go. Their own module's home, not `/app`, which for an agency
            that holds only the other module is a screen of four zeroes. */}
        <Link to={homeFor(modules)} className="btn btn-ghost">
          Back to your own work
        </Link>
      </div>
    </div>
  );
}

/**
 * The door, on the layout route.
 *
 * A pathless route wrapping everything under `/app`, so the rail, the bar and the switcher are all
 * still drawn around the refusal — somebody who typed the wrong half of the product should not
 * lose the half they have while they read about it. An address neither module owns — the
 * dashboard, payments, settings, the team — falls through untouched, because `moduleForPath`
 * answers `null` for all of those by design.
 */
export function ModuleDoor() {
  const { pathname } = useLocation();
  const { modules } = useSession();
  const owner = moduleForPath(pathname);

  if (owner && !modules.includes(owner)) return <ModuleLockedPage code={owner} />;
  return <Outlet />;
}
